import { ButtonComponent } from '@shared/components/ui/button';
import { Component,  effect, inject, Injector, input, InputSignal, model, ModelSignal, OnInit, output, OutputEmitterRef, Signal, TemplateRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { modalAnimation } from '@shared/animations';
import { SwitchComponent } from '@shared/components/ui/switch';
import { SkeletonLoaderComponent } from "@shared/components/ui/skeleton-loader";
import { UiStore } from '@theme/data-access';

type TSize = 'normal' | 'large' | 'fit-content' | 'medium' | 'x-large';
export type TButton = {
  type?: 'link' | 'primary' | 'secondary' | 'destructive' | 'ghost' | 'warning' | 'danger' | 'info' | 'success' | 'outline',
  state?: 'default' | 'disabled',
  size?: 'small' | 'default',
  label?: string,
  hide?: boolean,
  disabled?: boolean,
  action?: () => void,
  loadingOnsignal?: Signal<boolean>,
  loading?: boolean,
  icon?: {iconName: string, direction: 'left' | 'right'},
} | boolean;

/**
 * A modal is a UI component that displays content in a layer above the main application.
 * It is typically used to capture user input, display information, or require user interaction before continuing.
 * Modals are often used for forms, alerts, dialogs, and other interactive elements.
 * They can be customized with various properties such as title, subtitle, size, and shadow effects.
 * Modals can be shown or hidden based on user actions or application state.
 * They usually include a close button or mechanism to dismiss the modal and return to the main application.
 */
@Component({
  selector: 'lib-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [CommonModule, ButtonComponent, TranslateModule, SwitchComponent, SkeletonLoaderComponent],
  animations: modalAnimation
})
export class ModalComponent implements OnInit {
  private readonly context = inject(Injector);
  public _active: boolean = false;
  public readonly backgroundMode = input<'default'| 'access-point'>('default');
  public readonly skeleton = input<boolean>(false);
  public readonly isMobile = inject(UiStore).isMobile;

  /**
   * Indicates the active state of the toggle.
   * @default false
   */
  public readonly switchActive = model<boolean>(null);

  /**
   * Emits when the switch value changes.
   */
  public readonly switchValueChanged: OutputEmitterRef<boolean> = output<boolean>();

  ngOnInit(): void {
    this.subscribeToSignals();
  }

  /**dis
   * Subscribes to the signals and closes the modal when the signal is true.
   */
  private subscribeToSignals(): void {
    if (this.closeOnSignal() && this.closeOnSignal().length > 0) {
      this.closeOnSignal().forEach((signal) => {
        effect(() => {
          if (signal()) {
            this.close.emit();
            this.show.set(false);
          }
        }, { injector: this.context });
      });
    }
  }

  /**
   * The content of the modal.
   * @default null
   * @example <ng-template #content>...</ng-template>
   */
  public readonly contentRef: InputSignal<TemplateRef<HTMLElement>> = input<TemplateRef<HTMLElement>>(null);

  /**
   * The header of the modal.
   * @default null
   * @example <ng-template #header>...</ng-template>
   */
  public readonly headerRef: InputSignal<TemplateRef<HTMLElement>> = input<TemplateRef<HTMLElement>>(null);

  /**
   * The footer of the modal.
   * @default null
   * @example <ng-template #footer>...</ng-template>
   */
  public readonly footerRef: InputSignal<TemplateRef<HTMLElement>> = input<TemplateRef<HTMLElement>>(null);

  /**
   * The visibility of the modal.
   * @default false
   */
  public readonly show: ModelSignal<boolean> = model<boolean>(false);

  /**
   * The title of the modal.
   * @default '''
   */
  public readonly title: InputSignal<string> = input<string>('');
  /**
   * The subtitle of the modal.
   * @default ''
   */
  public readonly subtitle: InputSignal<string> = input<string>('');
  /**
   * The size of the modal.
   * @default 'normal'
   */
  public readonly size: InputSignal<TSize> = input<TSize>('normal');

  public readonly switchLabel: InputSignal<string> = input<string>(null);

  public readonly id: InputSignal<string> = input<string>(null);
  /**
   * The button of destructive type positioned on the left side of the footer.
   * @example { type: 'primary', state: 'default', size: 'default', label: 'COMPONENTS.BUTTON.CONFIRM' }
   * @default false
   */
  public readonly leftButton: InputSignal<TButton> = input<TButton>(false);

  public readonly rightButton: InputSignal<TButton> = input<TButton>(false);

  /**
   * Emits when the destructive button is clicked.
   */
  public readonly leftButtonClicked: OutputEmitterRef<void> = output<void>();

  public readonly rightButtonClicked: OutputEmitterRef<void> = output<void>();
  /**
   * The button of outline type positioned on the right side of the footer.
   * This button is used to cancel the modal.
   * @example { type: 'outline', state: 'default', size: 'default', label: 'COMPONENTS.BUTTON.CANCEL' }
   * @default false
   */
  public readonly primaryButton: InputSignal<TButton> = input<TButton>(false);
  /**
   * Emits when the cancel button is clicked.
   */
  public readonly primaryButtonClicked: OutputEmitterRef<void> = output<void>();

  /**
   * The button of primary type positioned on the right side of the footer.
   * @example { type: 'primary', state: 'default', size: 'default', label: 'COMPONENTS.BUTTON.CONFIRM' }
   * @default false
   */
  public readonly secondaryButton = model<TButton>(false);
  /**
   * Emits when the confirm button is clicked.
   */
  public readonly secondaryButtonClicked: OutputEmitterRef<void> = output<void>();

  /**
   * Emits when the close button is clicked.
   */
  public readonly close: OutputEmitterRef<void> = output<void>();

  /**
   * The modal can be closed by clicking on the backdrop.
   * @default true
   */
  public readonly canCloseWithoutAction: InputSignal<boolean> = input<boolean>(true);

  /**
   * The modal can be closed based on a signal.
   * @default []
   */
  public readonly closeOnSignal: InputSignal<Signal<boolean>[]> = input<Signal<boolean>[]>([]);


  public toggleModal(): void {
    this.show.set(!this.show());
  }

  public cancel(): void {
    this.primaryButton()?.['action']?.();
    this.primaryButtonClicked.emit();
  }

  public confirm(): void {
    this.secondaryButton()?.['action']?.();
    this.secondaryButtonClicked.emit();
  }

  public leftButtonClick(): void {
    this.leftButton()?.['action']?.();
    this.leftButtonClicked.emit();
  }

  public rightButtonClick(): void {
    this.rightButton()?.['action']?.();
    this.rightButtonClicked.emit();
  }

  public switchChanged(event: boolean): void {
    if (this.switchActive() !== null) {
      this.switchActive.set(event);
    }
    this.switchValueChanged.emit(event);
  }
}

